import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleLogin: (data) => api.post('/auth/google', data),
  setRole: (data) => api.put('/auth/set-role', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  requestReset: (data) => api.post('/auth/password-reset', data),
  resetPassword: (data) => api.post('/auth/password-reset/confirm', data),
};

// Services
export const servicesAPI = {
  getAll: (params) => api.get('/services', { params }),
  getById: (id) => api.get(`/services/${id}`),
  getMy: () => api.get('/services/my'),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Products
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getMy: () => api.get('/products/my'),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Bookings
export const bookingsAPI = {
  create: (data) => api.post('/bookings', data),
  getMy: (params) => api.get('/bookings/my', { params }),
  getProvider: (params) => api.get('/bookings/provider', { params }),
  getProviderStats: () => api.get('/bookings/provider/stats'),
  getById: (id) => api.get(`/bookings/${id}`),
  updateStatus: (id, data) => api.put(`/bookings/${id}/status`, data),
};

// Reviews
export const reviewsAPI = {
  create: (data) => api.post('/reviews', data),
  getByProvider: (id, params) => api.get(`/reviews/provider/${id}`, { params }),
  getByService: (id, params) => api.get(`/reviews/service/${id}`, { params }),
  getByProduct: (id, params) => api.get(`/reviews/product/${id}`, { params }),
};

// Messages
export const messagesAPI = {
  getConversations: () => api.get('/messages/conversations'),
  getMessages: (conversationId, params) => api.get(`/messages/${conversationId}`, { params }),
  send: (data) => api.post('/messages', data),
  startConversation: (data) => api.post('/messages/conversation', data),
  getUnreadCount: () => api.get('/messages/unread-count'),
};

// Payments
export const paymentsAPI = {
  initiate: (data) => api.post('/payments', data),
  getMy: (params) => api.get('/payments/my', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  confirm: (id, data) => api.put(`/payments/${id}/confirm`, data),
};

// Notifications
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
};

// Categories
export const categoriesAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getById: (id) => api.get(`/categories/${id}`),
};

// Providers
export const providersAPI = {
  search: (params) => api.get('/providers/search', { params }),
  getById: (id) => api.get(`/providers/${id}`),
};

// Orders
export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  getMy: (params) => api.get('/orders/my', { params }),
  getSeller: (params) => api.get('/orders/seller', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  updateStatus: (id, data) => api.put(`/orders/${id}/status`, data),
};

// Favorites
export const favoritesAPI = {
  toggle: (data) => api.post('/favorites/toggle', data),
  getAll: () => api.get('/favorites'),
  getIds: () => api.get('/favorites/ids'),
};

// Locations
export const locationsAPI = {
  getAll: () => api.get('/locations'),
  getProvinces: () => api.get('/locations/provinces'),
  getDistricts: (provinceId) => api.get(`/locations/provinces/${provinceId}/districts`),
  getSectors: (districtId) => api.get(`/locations/districts/${districtId}/sectors`),
  // Admin
  createProvince: (data) => api.post('/locations/provinces', data),
  updateProvince: (id, data) => api.put(`/locations/provinces/${id}`, data),
  deleteProvince: (id) => api.delete(`/locations/provinces/${id}`),
  createDistrict: (data) => api.post('/locations/districts', data),
  updateDistrict: (id, data) => api.put(`/locations/districts/${id}`, data),
  deleteDistrict: (id) => api.delete(`/locations/districts/${id}`),
  createSector: (data) => api.post('/locations/sectors', data),
  updateSector: (id, data) => api.put(`/locations/sectors/${id}`, data),
  deleteSector: (id) => api.delete(`/locations/sectors/${id}`),
};

// Admin
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  toggleUserStatus: (id, data) => api.put(`/admin/users/${id}/status`, data),
  updateUserRole: (id, data) => api.put(`/admin/users/${id}/role`, data),
  getServices: (params) => api.get('/admin/services', { params }),
  toggleServiceStatus: (id, data) => api.put(`/admin/services/${id}/status`, data),
  deleteService: (id) => api.delete(`/admin/services/${id}`),
  getProducts: (params) => api.get('/admin/products', { params }),
  toggleProductStatus: (id, data) => api.put(`/admin/products/${id}/status`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  getBookings: (params) => api.get('/admin/bookings', { params }),
  getOrders: (params) => api.get('/admin/orders', { params }),
  getPayments: (params) => api.get('/admin/payments', { params }),
  createCategory: (data) => api.post('/categories', data),
  updateCategory: (id, data) => api.put(`/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/categories/${id}`),
  // Reports
  getReports: (params) => api.get('/admin/reports', { params }),
  updateReport: (id, data) => api.put(`/admin/reports/${id}`, data),
  // Subscriptions
  getSubscriptions: (params) => api.get('/admin/subscriptions', { params }),
  getSubscriptionPlans: () => api.get('/admin/subscription-plans'),
  updateSubscriptionPlans: (plans) => api.put('/admin/subscription-plans', { plans }),
  // Listing Approval
  getPendingListings: () => api.get('/admin/pending-listings'),
  approveService: (id) => api.put(`/admin/services/${id}/approve`),
  rejectService: (id) => api.put(`/admin/services/${id}/reject`),
  approveProduct: (id) => api.put(`/admin/products/${id}/approve`),
  rejectProduct: (id) => api.put(`/admin/products/${id}/reject`),
  // Settings
  getSettings: () => api.get('/admin/settings'),
  uploadBranding: (formData) => api.post('/admin/upload-branding', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateSettings: (settings) => api.put('/admin/settings', { settings }),
  // Ads
  getAds: () => api.get('/admin/ads'),
  createAd: (formData) => api.post('/admin/ads', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateAd: (id, formData) => api.put(`/admin/ads/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  toggleAd: (id) => api.put(`/admin/ads/${id}/toggle`),
  deleteAd: (id) => api.delete(`/admin/ads/${id}`),
};

// Subscriptions (user-facing)
export const subscriptionsAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  getMy: () => api.get('/subscriptions/my'),
  subscribe: (data) => api.post('/subscriptions/subscribe', data),
  initiatePayment: (data) => api.post('/subscriptions/initiate-payment', data),
  verifyPayment: (transactionId) => api.get(`/subscriptions/verify-payment/${transactionId}`),
  cancel: (id) => api.put(`/subscriptions/${id}/cancel`),
};

// Reports (user-facing)
export const reportsAPI = {
  create: (data) => api.post('/reports', data),
  getMy: () => api.get('/reports/my'),
};


// Views
export const viewsAPI = {
  record: (data) => api.post('/views', data),
  getCount: (item_type, item_id) => api.get(`/views/${item_type}/${item_id}`),
  getBatch: (items) => api.post('/views/batch', { items }),
};

export const recommendationsAPI = {
  getForYou: (params) => api.get('/recommendations/for-you', { params }),
  getInterests: () => api.get('/recommendations/interests'),
  updateDealsOpt: (opt_in) => api.put('/recommendations/deals-opt', { opt_in }),
};

export const adsAPI = {
  getByPlacement: (placement) => api.get(`/ads/placement/${placement}`),
  getBatch: (placements) => api.get('/ads/batch', { params: { placements: placements.join(',') } }),
  recordClick: (id) => api.post(`/ads/${id}/click`),
};

export default api;


export const settingsAPI = {
  getPublic: () => api.get('/settings/public'),
};
